import React from "react";
import { ExecutionFilters, ExecutionStatus } from "@/types/workflow";
import { Workflow } from "@/types/workflow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, X, Filter } from "lucide-react";

interface ExecutionFiltersBarProps {
  filters: ExecutionFilters;
  onFiltersChange: (filters: ExecutionFilters) => void;
  workflows: Workflow[];
}

const statusOptions: { value: ExecutionStatus; label: string }[] = [
  { value: "success", label: "Succesvol" },
  { value: "failed", label: "Mislukt" },
  { value: "running", label: "Actief" },
  { value: "pending", label: "Wachtend" },
  { value: "waiting", label: "Wacht op input" },
  { value: "cancelled", label: "Geannuleerd" },
];

export const ExecutionFiltersBar: React.FC<ExecutionFiltersBarProps> = ({
  filters,
  onFiltersChange,
  workflows,
}) => {
  const activeFilterCount = [
    filters.status?.length,
    filters.workflow_id,
    filters.search,
  ].filter(Boolean).length;

  const handleStatusToggle = (status: ExecutionStatus) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status];
    
    onFiltersChange({
      ...filters,
      status: newStatuses.length > 0 ? newStatuses : undefined,
    });
  };

  const handleClearFilters = () => {
    onFiltersChange({});
  };

  return (
    <div className="space-y-4">
      {/* Search and Workflow Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek op workflow naam of ID..."
            className="pl-8"
            value={filters.search || ""}
            onChange={(e) => onFiltersChange({ ...filters, search: e.target.value || undefined })}
          />
        </div>
        <Select
          value={filters.workflow_id || "all"}
          onValueChange={(value) => 
            onFiltersChange({ 
              ...filters, 
              workflow_id: value === "all" ? undefined : value 
            })
          }
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Alle workflows" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle workflows</SelectItem>
            {workflows.map((workflow) => (
              <SelectItem key={workflow.id} value={workflow.id}>
                {workflow.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Status Filter Badges */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground flex items-center gap-1">
          <Filter className="h-4 w-4" />
          Status:
        </span>
        {statusOptions.map((option) => (
          <Badge
            key={option.value}
            variant={filters.status?.includes(option.value) ? "default" : "outline"}
            className="cursor-pointer hover:bg-primary/80"
            onClick={() => handleStatusToggle(option.value)}
          >
            {option.label}
          </Badge>
        ))}
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            Wis filters
          </Button>
        )}
      </div>
    </div>
  );
};
